import * as Comlink from 'comlink';
import { tableFromIPC } from 'apache-arrow';
import type { EngineWorkerApi, EngineTable, TableProfile } from '@numbernode/engine-duckdb';
import DuckDbWorker from './duckdb.worker?worker';

export type QueryResult = {
  columns: string[];
  rows: Array<Record<string, unknown>>;
  rowCount: number;
  elapsedMs: number;
  cached: boolean;
  sql: string;
};

let worker: Worker | undefined;
let remote: Comlink.Remote<EngineWorkerApi> | undefined;
let initialisation: Promise<{ bundle: string; version: string }> | undefined;

function normalise(value: unknown): unknown {
  if (typeof value === 'bigint') return Number.isSafeInteger(Number(value)) ? Number(value) : value.toString();
  if (value instanceof Date) return value.toISOString();
  if (ArrayBuffer.isView(value)) return Array.from(value as unknown as ArrayLike<number>);
  return value;
}

export function engine(): Comlink.Remote<EngineWorkerApi> {
  if (!remote) {
    worker = new DuckDbWorker();
    remote = Comlink.wrap<EngineWorkerApi>(worker);
  }
  return remote;
}

export function initialiseEngine() {
  initialisation ??= engine().initialise();
  return initialisation;
}

export async function query(sql: string): Promise<QueryResult> {
  await initialiseEngine();
  const payload = await engine().query(sql, crypto.randomUUID());
  const table = tableFromIPC(payload.ipc);
  const columns = table.schema.fields.map((field) => field.name);
  const rows = table.toArray().map((row) => Object.fromEntries(columns.map((column) => [column, normalise((row as Record<string, unknown>)[column])])));
  return { columns, rows, rowCount: payload.rowCount, elapsedMs: payload.elapsedMs, cached: payload.cached, sql };
}

export async function importIntoEngine(name: string, tableName: string, format: string, bytes: Uint8Array): Promise<EngineTable> {
  await initialiseEngine();
  return engine().importFile({ name, tableName, format, bytes });
}

export async function profileTable(tableName: string): Promise<TableProfile> {
  await initialiseEngine();
  return engine().profile(tableName);
}

export async function execute(sql: string): Promise<void> {
  await initialiseEngine();
  await engine().execute(sql);
}

export function terminateEngine(): void {
  worker?.terminate();
  worker = undefined;
  remote = undefined;
  initialisation = undefined;
}
